import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import { useAuth } from '../hooks';
import { authorities, authorityCategories } from '../data/authoritiesData';
import '../styles/Authorities.css';

const Authorities = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter authorities based on category and search
  const filteredAuthorities = authorities.filter(authority => {
    const matchesCategory = selectedCategory === 'All' || authority.category === selectedCategory;
    const matchesSearch = searchQuery === '' ||
      authority.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      authority.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      authority.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedCategory('All');
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />
      <div className="app">
        <Sidebar />
        <div className="main-content">
          <div className="content-area">

            {/* Hero Section */}
            <div className="authorities-hero">
              <div className="authorities-hero-content">
                <div className="authorities-hero-label">AUTHORITIES & REGULATORS</div>
                <h1 className="authorities-hero-title">Regulatory Authorities Directory</h1>
                <p className="authorities-hero-description">
                  Browse {authorities.length} regulatory authorities across EU frameworks including ESMA, EBA, EIOPA, EDPB, and ENISA.
                  Search by name, code, or description to find the relevant supervisory body.
                </p>
              </div>
            </div>

            {/* Search Section */}
            <div className="authorities-search-section">
              <form onSubmit={handleSearch} className="authorities-search-form">
                <div className="search-input-wrapper">
                  <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <input
                    type="text"
                    className="authorities-search-input"
                    placeholder="Search authorities by name, code, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="clear-button"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                  <button type="submit" className="search-button">
                    Search
                  </button>
                </div>
              </form>

              {/* Filters */}
              <div className="authorities-filters">
                <div className="filter-group">
                  <label className="filter-label">Authority Type</label>
                  <select
                    className="filter-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {authorityCategories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-stats">
                  <span className="stats-count">{filteredAuthorities.length}</span>
                  <span className="stats-label">authorities found</span>
                </div>
              </div>
            </div>

            {/* Authorities Grid */}
            <div className="authorities-section">
              {filteredAuthorities.length === 0 ? (
                <div className="empty-state">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="empty-icon">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                  </svg>
                  <p>No authorities found matching your search criteria.</p>
                  <p className="empty-hint">Try adjusting your filters or search query.</p>
                  <button onClick={handleClearSearch} className="reset-filters-btn">
                    Clear Filters
                  </button>
                </div>
              ) : (
                <div className="authorities-grid">
                  {filteredAuthorities.map(authority => (
                    <div key={authority.id} className="authority-card">
                      <div className="authority-header">
                        <div className="authority-type-badge">
                          {authority.type}
                        </div>
                        <div className="authority-code">{authority.code}</div>
                      </div>

                      <h3 className="authority-name">{authority.fullName}</h3>

                      <p className="authority-description">{authority.description}</p>

                      <div className="authority-meta">
                        <div className="meta-item">
                          <span className="meta-label">Established:</span>
                          <span className="meta-value">{authority.established}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Headquarters:</span>
                          <span className="meta-value">{authority.headquarters}</span>
                        </div>
                        <div className="meta-item">
                          <span className="meta-label">Category:</span>
                          <span className="meta-value">{authority.category}</span>
                        </div>
                      </div>

                      <div className="authority-responsibilities">
                        <h4>Key Responsibilities</h4>
                        <ul>
                          {authority.responsibilities.slice(0, 3).map((resp, idx) => (
                            <li key={idx}>{resp}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="authority-frameworks">
                        <span className="frameworks-label">FRAMEWORKS</span>
                        <div className="frameworks-tags">
                          {authority.frameworks.map(framework => (
                            <span key={framework} className="framework-tag">{framework}</span>
                          ))}
                        </div>
                      </div>

                      <div className="authority-footer">
                        <a
                          href={authority.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="authority-link"
                        >
                          View Website
                        </a>
                        {authority.email && (
                          <a
                            href={`mailto:${authority.email}`}
                            className="authority-button"
                          >
                            Contact
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Authorities;
